import React, { useState, useEffect, useMemo, useRef } from 'react';
import Pagination from '../../components/Pagination.tsx';
import styles from '../../styles/MateriasStyles/PantallaDeTareas.v2.module.css';
import StatusBadge from './StatusBadge';
import { getConceptosCobro, deleteConceptoCobro } from '../../api/conceptosCobro';


const ConceptosTable = ({ conceptos = [], loading = false, onView, onEdit, onReload, onDuplicate, isSearching = false, query = '', campus = '' }) => {
  // PAGINATION LOGIC
  const [page, setPage] = useState(1);
  const pageSize = 8;

  // Crear clave única basada en IDs y campus para detectar cambios reales
  const conceptosKey = useMemo(() => {
    return (conceptos || []).map(c => c.id).sort().join(',') + '|' + campus;
  }, [conceptos, campus]);

  const prevConceptosKeyRef = useRef(conceptosKey);

  // Reiniciar página solo cuando realmente cambia la lista o el campus
  useEffect(() => {
    if (prevConceptosKeyRef.current !== conceptosKey) {
      setPage(1);
      prevConceptosKeyRef.current = conceptosKey;
    }
  }, [conceptosKey]);
  // Filtrar por campus si está definido y no es 'Todos'
  const conceptosFiltrados = campus && campus !== 'Todos'
    ? conceptos.filter(c => c.campus_nombre === campus)
    : conceptos;
  const totalPages = Math.max(1, Math.ceil(conceptosFiltrados.length / pageSize));
  const paginated = conceptosFiltrados.slice((page - 1) * pageSize, page * pageSize);

  const handleDelete = async (conceptoId) => {
    if (!window.confirm('¿Está seguro de eliminar este concepto de cobro?')) {
      return;
    }
    try {
      await deleteConceptoCobro(conceptoId);
      window.alert('El registro fue eliminado con éxito');
      // Refresca los datos llamando a onReload si está definido
      if (onReload) {
        onReload();
      }
    } catch (error) {
      console.error('Error al eliminar concepto:', error);
      alert('Error al eliminar el concepto de cobro');
    }
  };

  // Botones de acción con el mismo estilo que MateriasTable
  const ActionButtons = ({ concepto }) => (
    <div className={styles.actionButtons}>
      <button aria-label="Ver detalles" className={styles.actionButton} onClick={() => onView && onView(concepto.id)}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={styles.actionIcon}
        />
      </button>
      <button aria-label="Editar" className={styles.actionButton} onClick={() => onEdit && onEdit(concepto.id)}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
          alt="Editar"
          className={styles.actionIcon}
        />
      </button>
      <button aria-label="Duplicar" className={styles.actionButton} title="Duplicar concepto" onClick={() => onDuplicate && onDuplicate(concepto.id)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.actionIcon}>
          <rect x="9" y="9" width="13" height="13" rx="2" stroke="#555" strokeWidth="2"/>
          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#555" strokeWidth="2"/>
        </svg>
      </button>
      <button aria-label="Eliminar" className={styles.actionButton} onClick={() => handleDelete && handleDelete(concepto.id)}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
          alt="Eliminar"
          className={styles.actionIcon}
        />
      </button>
    </div>
  );

    return (
      <div className={styles.tableContainer} style={{ position: 'relative', minHeight: '320px', zIndex: 1 }}>
        <div className={styles.tableHeader}>
          <div className={styles.tableTitle}>
            <div className={styles.tableTitleIcon}>
              <div className={styles.iconContainerOrange}>
                <span>💰</span>
              </div>
            </div>
            <h2 className={styles.tableTitleText}>Conceptos de Cobro</h2>
          </div>
        </div>

        <div
          className={styles.tableHeaderRow}
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(100px, 0.8fr) minmax(140px, 1.2fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(140px, 1.2fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(120px, 1fr)',
            columnGap: '12px',
            alignItems: 'center',
            padding: '12px 20px',
            minHeight: '48px',
          }}
        >
          <div className={styles.columnHeader}><span>Código</span></div>
          <div className={styles.columnHeader}><span>Nombre</span></div>
          <div className={styles.columnHeader}><span>Campus</span></div>
          <div className={styles.columnHeader}><span>Categoría</span></div>
          <div className={styles.columnHeaderWide}><span>Subcategoría</span></div>
          <div className={styles.columnHeader}><span>Aplicación</span></div>
          <div className={styles.columnHeader}><span>Sección</span></div>
          <div className={styles.columnHeader}><span>Ciclo</span></div>
          <div className={styles.columnHeader}><span>Estado</span></div>
          <span className={styles.columnHeaderWide + ' ' + styles.stickyActionsHeader}>Acciones</span>
        </div>

        <div className={styles.tableBody}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>Cargando...</div>
          ) : paginated.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>No hay conceptos de cobro</div>
          ) : (
            paginated.map((concepto, index) => (
              <div
                key={concepto.id}
                className={styles.tableRow}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(100px, 0.8fr) minmax(140px, 1.2fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(140px, 1.2fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr) minmax(120px, 1fr)',
                  columnGap: '12px',
                  alignItems: 'center',
                  padding: '10px 20px',
                  borderBottom: '1px solid #eee'
                }}
              >
                <span className={styles.cellData}>{concepto.codigo}</span>
                <span className={styles.cellData}>{concepto.nombre}</span>
                <span className={styles.cellData}>{concepto.campus_nombre || '-'}</span>
                <span className={styles.cellData}>{concepto.categoria}</span>
                <span className={styles.cellData}>{concepto.subcategoria}</span>
                <span className={styles.cellData}>{concepto.aplicacion}</span>
                <span className={styles.cellData}>{concepto.seccion_nombre || '-'}</span>
                <span className={styles.cellData}>{concepto.ciclo}</span>
                <div className={styles.statusCell}>
                  <div className={
                    concepto.estado === 'ACTIVO'
                      ? styles.statusActive
                      : styles.statusInactive
                  }>
                    <span>{concepto.estado || 'INACTIVO'}</span>
                  </div>
                </div>
                <div className={styles.actionButtons + ' ' + styles.stickyActionsCell}>
                  <ActionButtons concepto={concepto} />
                </div>
              </div>
            ))
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
  );
};

export default ConceptosTable;
